/**
 * The ConnectWallet component
 */
import { BowlineBrandLabel } from "@bkry/bowline-components/BowlineBrandLabel";
import { AuthConnectButton } from "@bkry/bowline-components/ConnectWalletButton";
import {
  CoinbaseIcon,
  LedgerIcon,
  MetaMaskIcon,
  WalletConnectIcon,
} from "@bkry/bowline-components/Icon";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { FcGoogle } from "react-icons/fc";
import {
  Badge,
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  Row,
} from "reactstrap";
import BowlineWalletConnectInfo from "./utils/BowlineWalletConnectInfo";
import CoinbaseConnectInfo from "./utils/CoinbaseConnectInfo";
import LedgerConnectInfo from "./utils/LedgerConnectInfo";
import MetamaskConnectInfo from "./utils/MetamaskConnectInfo";
import WalletConnectInfo from "./utils/WalletConnectInfo";
import { getEnv } from "@bkry/bowline-utils";
import { useSelector } from "react-redux";

/**
 * Stateless react component to render the ConnectWallet
 * @param {Object} props the props passed to the component.
 */

const ConnectWallet = (props, context) => {
  const {
    className,
    buttonId,
    buttonLabel,
    buttonColor,
    buttonSize,
    block,
    suggestedChainId,
  } = props;
  const [showModal, setShowModal] = useState(false);
  const [tab, setTab] = useState(0);
  const REACT_APP_PROJECT_ID = getEnv("REACT_APP_PROJECT_ID");
  const suggestedChainOptions = useSelector(
    (state) => state.projects.defaultNetworks[REACT_APP_PROJECT_ID]
  );

  return (
    <>
      <Button
        className={className}
        onClick={() => setShowModal(true)}
        color={buttonColor}
        size={buttonSize}
        block={block}
        id={buttonId}
      >
        {buttonLabel ? buttonLabel : context.t("Connect to Wallet")}
      </Button>
      <Modal
        isOpen={showModal}
        size="xl"
        toggle={() => setShowModal(!showModal)}
        fullscreen="lg"
      >
        <ModalBody className="p-0 connect-modal-body">
          <button
            className="btn-close position-absolute top-0 end-0 p-4"
            onClick={() => setShowModal(false)}
            aria-label={context.t("close")}
          ></button>
          <Row className="g-0 modal-row">
            <Col xs={{ size: 12 }} lg={{ size: 4 }} className="left-side">
              <p className="h4 text-center fw-bold mb-5">
                {context.t("Select a Wallet")}
              </p>
              <Nav className="flex-lg-column justify-content-center">
                <NavItem className="me-2 me-lg-0">
                  <Button
                    onClick={() => setTab(1)}
                    color="transparent"
                    className={`text-start py-2 mb-3 fw-bold nav-button border-0 rounded-0 ps-lg-5 ${
                      tab === 1 && "active"
                    }`}
                    block
                  >
                    <MetaMaskIcon width={32} className="me-lg-4" />
                    <span className="d-none d-lg-inline h5 fw-bold">
                      {context.t("Metamask")}
                    </span>
                  </Button>
                </NavItem>
                <NavItem className="me-2 me-lg-0">
                  <Button
                    onClick={() => setTab(6)}
                    color="transparent"
                    className={`text-start py-2 mb-3 fw-bold nav-button border-0 rounded-0 ps-lg-5 ${
                      tab === 6 && "active"
                    }`}
                    block
                  >
                    <LedgerIcon width={32} className="me-lg-4" />
                    <span className="d-none d-lg-inline h5 fw-bold">
                      {context.t("Ledger")}
                    </span>
                    <Badge
                      size="sm"
                      color="secondary"
                      style={{ fontSize: "0.6rem" }}
                      className="ms-2"
                    >
                      BETA
                    </Badge>
                  </Button>
                </NavItem>
                <NavItem className="me-2 me-lg-0">
                  <Button
                    onClick={() => setTab(5)}
                    color="transparent"
                    className={`text-start py-2 mb-3 fw-bold nav-button border-0 rounded-0 ps-lg-5 ${
                      tab === 5 && "active"
                    }`}
                    block
                  >
                    <CoinbaseIcon width={32} className="me-lg-4" />
                    <span className="d-none d-lg-inline h5 fw-bold">
                      {context.t("Coinbase Wallet")}
                    </span>
                    <Badge
                      size="sm"
                      color="secondary"
                      style={{ fontSize: "0.6rem" }}
                      className="ms-2"
                    >
                      BETA
                    </Badge>
                  </Button>
                </NavItem>

                <NavItem className="me-2 me-lg-0">
                  <Button
                    onClick={() => setTab(3)}
                    color="transparent"
                    className={`text-start py-2 mb-3 fw-bold nav-button border-0 rounded-0 ps-lg-5 ${
                      tab === 3 && "active"
                    }`}
                    block
                  >
                    <WalletConnectIcon width={32} className="me-lg-4" />
                    <span className="d-none d-lg-inline h5 fw-bold">
                      {context.t("Wallet Connect")}
                    </span>
                  </Button>
                </NavItem>
                {suggestedChainOptions?.wta_id && (
                  <>
                    <div className="mt-5 mb-3 d-none d-lg-flex px-lg-5 align-items-center text-center">
                      <span
                        className="border-bottom border-muted mx-3 mb-2"
                        style={{ width: "30px" }}
                      ></span>
                      <span className="d-none d-lg-inline small text-muted">
                        {context.t("Social Connect")}
                      </span>
                      <span
                        className="border-bottom border-muted mx-3 mb-2"
                        style={{ width: "30px" }}
                      ></span>
                    </div>
                    <NavItem className="me-2 me-lg-0">
                      <Button
                        onClick={() => setTab(2)}
                        className={`text-start py-2 mb-3 fw-bold nav-button border-0 rounded-0 ps-lg-5 ${
                          tab === 2 && "active"
                        }`}
                        block
                        color="transparent"
                      >
                        <FcGoogle
                          alt={context.t("Google")}
                          size={32}
                          className="me-lg-4"
                        />
                        <span className="d-none d-lg-inline h5 fw-bold">
                          {context.t("Google")}
                        </span>
                      </Button>
                    </NavItem>
                  </>
                )}
                {/* <NavItem className="me-2 me-lg-0">
                  <Button
                    onClick={() => setTab(4)}
                    className={`text-start py-2 mb-3 fw-bold nav-button border-0 rounded-0 ps-lg-5 ${
                      tab === 4 && "active"
                    }`}
                    block
                    color="transparent"
                  >
                    <BsFacebook
                      alt={context.t("Facebook")}
                      size={32}
                      className="me-lg-4"
                    />
                    <span className="d-none d-lg-inline h5 fw-bold">
                      {context.t("Facebook")}
                    </span>
                  </Button>
                </NavItem> */}
              </Nav>
            </Col>
            <Col
              xs={{ size: 12 }}
              lg={{ size: 8 }}
              className="right-side px-lg-5"
            >
              {tab === 0 && <BowlineWalletConnectInfo />}
              {tab === 1 && (
                <MetamaskConnectInfo suggestedChainId={suggestedChainId} />
              )}
              {tab === 2 && (
                <AuthConnectButton suggestedChainId={suggestedChainId} />
              )}
              {tab === 3 && (
                <WalletConnectInfo suggestedChainId={suggestedChainId} />
              )}
              {/* {tab === 4 && <FacebookConnectButton />} */}
              {tab === 5 && (
                <CoinbaseConnectInfo suggestedChainId={suggestedChainId} />
              )}
              {tab === 6 && (
                <LedgerConnectInfo
                  closeConnectWallet={() => setShowModal(false)}
                  suggestedChainId={suggestedChainId}
                />
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex flex-column">
          <p>
            <BowlineBrandLabel />
          </p>
          <p className="small">
            <div
              className="d-inline me-1"
              dangerouslySetInnerHTML={{
                __html: context.t(
                  "By connecting your wallet you accept our <a href='https://www.bowline.app/tos/' target='_blank'>Terms of Service</a>."
                ),
              }}
            />
            <div
              className="d-inline"
              dangerouslySetInnerHTML={{
                __html: context.t(
                  "For more information on the processing of your personal data, please see our <a href='https://www.bowline.app/data-protection/' target='_blank'>Privacy Policy</a>."
                ),
              }}
            />
          </p>
        </ModalFooter>
      </Modal>
    </>
  );
};

/** define proptypes for the ConnectWallet */
ConnectWallet.propTypes = {
  className: PropTypes.string,
  buttonId: PropTypes.string,
  buttonLabel: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonSize: PropTypes.string,
  block: PropTypes.bool,
  suggestedChainId: PropTypes.number,
};

ConnectWallet.defaultProps = {
  buttonColor: "secondary",
  buttonSize: "md",
  block: false,
};

/**  define proptype for the 'translation' function  */
ConnectWallet.contextTypes = {
  t: PropTypes.func,
};

export default ConnectWallet;
